"use client";

import { motion } from "motion/react";

import { cn } from "@/lib/utils";

// =============================================================================

const FALLBACK_COLOR = "#ef5151";

/**
 * Three colors: light, mid and dark (the dark one is the backdrop). Missing
 * colors are derived from the first ones.
 */
function toPalette(colors: string[]): [string, string, string] {
  const [light = FALLBACK_COLOR, mid = light, dark] = colors;
  return [light, mid, dark ?? `color-mix(in srgb, ${mid} 14%, #000)`];
}

type Blob = {
  color: 0 | 1 | 2;
  className: string;
  /** Soft (0) to sharp (1) edge */
  edge: number;
  drift: { x: string[]; y: string[]; scale: number[]; rotate: number[] };
  duration: number;
};

// Large shapes drifting slowly, like an animated wallpaper: light and mid
// tones over the dark backdrop, crossed by dark waves
const BLOBS: Blob[] = [
  {
    color: 1,
    className: "top-[-45%] left-[-6%] h-[50cqw] w-[46cqw]",
    edge: 0.45,
    drift: {
      x: ["0%", "8%", "-4%", "0%"],
      y: ["0%", "7%", "12%", "0%"],
      scale: [1, 1.08, 0.95, 1],
      rotate: [0, 12, -6, 0],
    },
    duration: 26,
  },
  {
    color: 0,
    className: "top-[-30%] left-[4%] h-[34cqw] w-[30cqw]",
    edge: 0.5,
    drift: {
      x: ["0%", "12%", "4%", "0%"],
      y: ["0%", "10%", "-6%", "0%"],
      scale: [1, 0.92, 1.1, 1],
      rotate: [0, -10, 8, 0],
    },
    duration: 21,
  },
  {
    color: 0,
    className: "top-[8%] right-[-14%] h-[44cqw] w-[40cqw]",
    edge: 0.5,
    drift: {
      x: ["0%", "-10%", "4%", "0%"],
      y: ["0%", "-8%", "6%", "0%"],
      scale: [1, 0.92, 1.1, 1],
      rotate: [0, -14, 8, 0],
    },
    duration: 23,
  },
  {
    color: 1,
    className: "right-[18%] bottom-[-70%] h-[38cqw] w-[54cqw]",
    edge: 0.5,
    drift: {
      x: ["0%", "-12%", "6%", "0%"],
      y: ["0%", "-12%", "-4%", "0%"],
      scale: [1, 1.12, 0.94, 1],
      rotate: [0, 12, -10, 0],
    },
    duration: 30,
  },
  {
    // Light band sweeping across the middle
    color: 0,
    className: "top-[34%] left-[14%] h-[16cqw] w-[62cqw]",
    edge: 0.4,
    drift: {
      x: ["0%", "-8%", "6%", "0%"],
      y: ["0%", "18%", "-12%", "0%"],
      scale: [1, 1.1, 0.94, 1],
      rotate: [-16, -4, -24, -16],
    },
    duration: 27,
  },
  {
    // Dark wave crossing the light shapes
    color: 2,
    className: "top-[2%] left-[26%] h-[20cqw] w-[58cqw]",
    edge: 0.6,
    drift: {
      x: ["0%", "10%", "-6%", "0%"],
      y: ["0%", "-14%", "12%", "0%"],
      scale: [1, 1.12, 0.92, 1],
      rotate: [-28, -14, -36, -28],
    },
    duration: 24,
  },
  {
    color: 2,
    className: "bottom-[-40%] left-[-12%] h-[34cqw] w-[44cqw]",
    edge: 0.5,
    drift: {
      x: ["0%", "10%", "-4%", "0%"],
      y: ["0%", "-8%", "4%", "0%"],
      scale: [1, 1.1, 0.95, 1],
      rotate: [0, 10, -8, 0],
    },
    duration: 28,
  },
];

// =============================================================================

type MeshGradientProps = {
  colors: string[];
  /**
   * Side of the showcase the panel sits on: the layer spans the whole
   * showcase (100cqw) so that both panels show one continuous backdrop.
   */
  align: "left" | "right";
  className?: string;
};

/**
 * Animated backdrop built from the 2-3 colors of a project. Only transforms
 * are animated (compositor friendly); reduced motion freezes it.
 */
export function MeshGradient({ colors, align, className }: MeshGradientProps) {
  const palette = toPalette(colors);

  return (
    <div
      aria-hidden
      className={cn(
        "absolute inset-y-0 w-[100cqw] overflow-hidden",
        align === "left" ? "left-0" : "right-0",
        className
      )}
      style={{ backgroundColor: palette[2] }}
    >
      {BLOBS.map((blob, index) => (
        <motion.div
          key={index}
          className={cn("absolute rounded-full", blob.className)}
          style={{
            background: `radial-gradient(closest-side, ${palette[blob.color]} ${Math.round(blob.edge * 100)}%, transparent)`,
          }}
          animate={blob.drift}
          transition={{
            duration: blob.duration,
            ease: "easeInOut",
            repeat: Infinity,
          }}
        />
      ))}
      {/* Vignette for depth */}
      <div className="absolute inset-0 bg-[radial-gradient(120%_90%_at_50%_40%,transparent_55%,rgb(0_0_0/0.35))]" />
    </div>
  );
}

// =============================================================================
